/*
 * The dashboard extension set — the icons the Phase-1 sheet does not carry.
 *
 * 🔴 WHY THIS IS A SECOND FILE AND NOT SIX MORE LINES IN `icons.tsx`.
 *
 * `icons.tsx` is GENERATED from `docs/design/Kalqy Foundations (1).html` by
 * `scripts/generate-icons.mjs`, and `npm run icons:check` fails the build the
 * moment it drifts from that source. Adding a hand-drawn icon there would
 * either be reverted by the next `icons:generate` or would break the check —
 * so the delivered set stays exactly as delivered, and anything the product
 * needs beyond it lives here, where it can be reviewed as a hand-drawn thing.
 *
 * 🔴 IDENTICAL GEOMETRY, DELIBERATELY. Same 24px grid, same 2px round-cap
 * stroke, same `size`/`strokeWidth` props, same `currentColor`. §6's rule is
 * "one stroke set shared by both shells" — a second FILE is not a second SET
 * unless the geometry differs, and it does not. `Icon` is re-declared rather
 * than imported because `icons.tsx` does not export it, and exporting it would
 * mean editing a generated file to make this one shorter.
 *
 * 🔴 EVERY ICON HERE REPLACED AN EMOJI THAT WAS ALREADY ON A SCREEN.
 * `design-system.md` §12 lists "emoji as a UI icon" on the Never list and
 * `scripts/check-emoji.mjs` exists to enforce it, but the three adult
 * dashboards had drifted to ~120 of them — 🔍 for a search box, 📞 beside a
 * phone number, 🏆 over a leaderboard, 🥇 in a rank column. Each was a
 * font-stack lottery on the school laptops this is read on: the same screen
 * renders differently on Windows 10, Windows 11 and a Chromebook, and a few
 * of them (🪙, 🧩) have no glyph at all in some default stacks and fall back
 * to a hollow rectangle. These are the replacements, one per emoji that was
 * doing an interface job rather than naming a game.
 *
 * ⚠️ NOT GAME IDENTITY. The five game emoji stay exactly where they are —
 * §6 permits those and the curriculum files are on the allowlist for it.
 */
import type { SVGProps } from 'react'
import type { ReactNode } from 'react'

export interface DashIconProps extends Omit<SVGProps<SVGSVGElement>, 'children'> {
  /** 14–24px in DashboardShell. */
  size?: number
  /** 2 in DashboardShell, 2.4 where a row needs a touch more weight. */
  strokeWidth?: number
}

/** Same wrapper as the generated set, so the two are indistinguishable on a row. */
function Icon({
  size = 24,
  strokeWidth = 2,
  children,
  ...rest
}: DashIconProps & { children: ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...rest}
    >
      {children}
    </svg>
  )
}

/**
 * `search` — replaced 🔍 in the teacher roster, the school children screen and
 * the teachers screen.
 */
export const SearchIcon = (props: DashIconProps) => (
  <Icon {...props}>
    <circle cx="11" cy="11" r="7"></circle>
    <path d="m20 20-3.5-3.5"></path>
  </Icon>
)

/**
 * `calendar` — replaced 📅 beside "Last played" and on the attendance panels.
 */
export const CalendarIcon = (props: DashIconProps) => (
  <Icon {...props}>
    <rect x="3" y="5" width="18" height="16" rx="2.5"></rect>
    <path d="M3 10h18M8 3v4M16 3v4"></path>
  </Icon>
)

/**
 * `clock` — replaced ⏰ on the parent's "Learning time" tile.
 *
 * ⚠️ IT MEANS TIME, NEVER URGENCY. The parent tile it sits on prints "at
 * least N min", and an alarm-clock reading would turn a neutral count into a
 * deadline about somebody's four-year-old.
 */
export const ClockIcon = (props: DashIconProps) => (
  <Icon {...props}>
    <circle cx="12" cy="12" r="9"></circle>
    <path d="M12 7v5l3.5 2"></path>
  </Icon>
)

/**
 * `message` — replaced 💬 and 👩‍🏫 in the messages panels.
 *
 * 🔴 A SPEECH BUBBLE, NOT A PERSON. The generated set has `voice`, which is a
 * speaker cone and reads as broadcast — right for a class notice and wrong for
 * one conversation with one home. This is the second half of that pair.
 */
export const MessageIcon = (props: DashIconProps) => (
  <Icon {...props}>
    <path d="M20 15.5a2.5 2.5 0 0 1-2.5 2.5H9l-4 3v-3H6.5A2.5 2.5 0 0 1 4 15.5v-8A2.5 2.5 0 0 1 6.5 5h11A2.5 2.5 0 0 1 20 7.5Z"></path>
  </Icon>
)

/**
 * `phone` — replaced 📞 beside a parent's number on three screens.
 */
export const PhoneIcon = (props: DashIconProps) => (
  <Icon {...props}>
    <path d="M6.5 4h3l1.5 4-2 1.5a11 11 0 0 0 5.5 5.5L16 13l4 1.5v3a2 2 0 0 1-2.2 2A15.5 15.5 0 0 1 4 6.2 2 2 0 0 1 6 4Z"></path>
  </Icon>
)

/**
 * `flame` — replaced 🔥 on every streak figure.
 *
 * ⚠️ THE STREAK IS THE ONE METRIC A CHILD ALSO SEES, and the child's screen
 * draws it in `--color-leaf`. This carries no colour of its own for that
 * reason: the call site sets it, so the adult tiers and the play tier agree.
 */
export const FlameIcon = (props: DashIconProps) => (
  <Icon {...props}>
    <path d="M12 3s5 4 5 8.5a5 5 0 0 1-10 0C7 9 9 8 9 8s0 2 1.5 2C12 10 12 6 12 3Z"></path>
  </Icon>
)

/**
 * `medal` — replaced 🥇🥈🥉 in the leaderboard's rank column.
 *
 * 🔴 ONE SHAPE FOR ALL THREE PLACES, and the place is carried by the NUMERAL
 * beside it plus the pill's tone. Three different medal emoji made rank a
 * thing you read by colour alone, which §12 forbids: status is icon + colour
 * + text, and the text is the rank number.
 */
export const MedalIcon = (props: DashIconProps) => (
  <Icon {...props}>
    <circle cx="12" cy="14.5" r="5.5"></circle>
    <path d="M8.5 9 6 3h12l-2.5 6"></path>
  </Icon>
)

/**
 * `sparkle` — replaced 💡 over the insight panels on all three tiers.
 *
 * 🔴 IT IS NOT A LIGHTBULB, AND THAT IS THE POINT. A bulb reads as "idea" —
 * somebody's opinion. These panels carry a `generatedBy` attribution line
 * precisely because a parent must not mistake a model's output for their
 * teacher's judgement, and the icon is now the first half of that sentence.
 */
export const SparkleIcon = (props: DashIconProps) => (
  <Icon {...props}>
    <path d="M12 3.5 13.6 9 19 10.5 13.6 12 12 17.5 10.4 12 5 10.5 10.4 9Z"></path>
    <path d="M18 16.5 18.7 19l2.3.8-2.3.7-.7 2.5-.7-2.5-2.3-.7 2.3-.8Z"></path>
  </Icon>
)

/**
 * `coin` — replaced 🪙 on every coin figure.
 *
 * ⚠️ THE GENERATED SET HAS `point`, AND IT IS NOT THIS. `point` is the reward
 * pip a child collects mid-round; coins are the running total an adult reads
 * on a leaderboard. They were already drawn as two different things on the two
 * shells, and collapsing them here would make the tiers disagree.
 */
export const CoinIcon = (props: DashIconProps) => (
  <Icon {...props}>
    <ellipse cx="12" cy="7.5" rx="7" ry="3.5"></ellipse>
    <path d="M5 7.5v9c0 1.9 3.1 3.5 7 3.5s7-1.6 7-3.5v-9"></path>
    <path d="M5 12c0 1.9 3.1 3.5 7 3.5s7-1.6 7-3.5"></path>
  </Icon>
)

/**
 * `gamepad` — replaced 🎮 on the parent's "Games played" tile and its activity
 * rows.
 *
 * ⚠️ IT NAMES THE COUNT, NOT A GAME. Which game a row is about is said in
 * words beside it; this is the category marker for "these numbers are about
 * playing", the way `reports` marks a coverage figure.
 */
export const GamepadIcon = (props: DashIconProps) => (
  <Icon {...props}>
    <rect x="2.5" y="7" width="19" height="11" rx="4.5"></rect>
    <path d="M7 11v3M5.5 12.5h3M15.5 11.5h.01M18 13.5h.01"></path>
  </Icon>
)

/**
 * `clipboard-check` — replaced ✔ on the parent's "Sessions" tile.
 *
 * ⚠️ NOT `CheckIcon`. A bare tick beside a number reads as "this number is
 * correct". A sitting is a record that was kept, which is the clipboard.
 */
export const RegisterIcon = (props: DashIconProps) => (
  <Icon {...props}>
    <path d="M9 4.5h6M8 4.5H6.5A1.5 1.5 0 0 0 5 6v13a1.5 1.5 0 0 0 1.5 1.5h11A1.5 1.5 0 0 0 19 19V6a1.5 1.5 0 0 0-1.5-1.5H16"></path>
    <path d="m9 13 2 2 4-4"></path>
  </Icon>
)

/**
 * `cross` — the partner to the generated `CheckIcon` on the class register.
 *
 * 🔴 IT REPLACED A LITERAL `✕` IN THE JSX, which is the same font-stack lottery
 * this file exists to end: U+2715 is a dingbat, not part of any webfont this
 * app vendors, so "absent" was drawn by whatever the laptop happened to have.
 * The register is the one screen where the two marks must be told apart at a
 * glance, so both are now strokes on the same 24px grid.
 *
 * ⚠️ NOT A "CLOSE" ICON. Nothing here dismisses anything — this is the mark for
 * a child who is absent, and it sits beside the word, never instead of it.
 */
export const CrossIcon = (props: DashIconProps) => (
  <Icon {...props}>
    <path d="M6 6l12 12M18 6 6 18"></path>
  </Icon>
)

/** Every hand-drawn icon, keyed by name, for parity with the generated set. */
export const DASH_ICONS = {
  search: SearchIcon,
  calendar: CalendarIcon,
  clock: ClockIcon,
  message: MessageIcon,
  phone: PhoneIcon,
  flame: FlameIcon,
  medal: MedalIcon,
  sparkle: SparkleIcon,
  coin: CoinIcon,
  gamepad: GamepadIcon,
  register: RegisterIcon,
  cross: CrossIcon,
} as const

export type DashIconName = keyof typeof DASH_ICONS
